// Adapts GET /api/v2/learning_stats/daily_series into the shapes
// Dashboard.vue / DashboardChart.vue / ActivityList.vue need (spec §7 task
// 2-6). Kept separate from the legacy UserTrackingData/ActivityData models
// rather than force-fit into them: the new path intentionally changes the
// manual-correction contract (a separate additive correction, not an
// overwritable total — spec §5.4/§11), so reusing the old class would hide
// that distinction rather than express it.

export class LearningStatsDay {
  readonly date: string // 'YYYY-MM-DD'
  readonly confirmedActiveSeconds: number
  readonly estimatedActiveSeconds: number
  readonly manualAdjustmentSeconds: number
  readonly sessionCount: number
  readonly activeLessonCount: number
  readonly completedLessonCount: number
  readonly assessmentAttemptCount: number

  constructor(data: { [key: string]: any }) {
    this.date = data.date
    this.confirmedActiveSeconds = data.confirmedActiveSeconds ?? 0
    this.estimatedActiveSeconds = data.estimatedActiveSeconds ?? 0
    this.manualAdjustmentSeconds = data.manualAdjustmentSeconds ?? 0
    this.sessionCount = data.sessionCount ?? 0
    this.activeLessonCount = data.activeLessonCount ?? 0
    this.completedLessonCount = data.completedLessonCount ?? 0
    this.assessmentAttemptCount = data.assessmentAttemptCount ?? 0
  }

  get totalActiveSeconds(): number {
    return this.confirmedActiveSeconds + this.estimatedActiveSeconds + this.manualAdjustmentSeconds
  }

  get totalActiveMinutes(): number {
    return Math.round(this.totalActiveSeconds / 60)
  }
}

export default class LearningStatsData {
  readonly days: LearningStatsDay[] = []
  // Null means the school has no new-path measurement yet -> every day is a legacy estimate.
  readonly measurementStartDate: string | null = null

  constructor(data: { [key: string]: any }) {
    this.days = (data.days ?? []).map((day: any) => new LearningStatsDay(day))
    this.measurementStartDate = data.measurementStartDate ?? null
  }

  get labels(): string[] {
    return this.days.map((day) => day.date)
  }

  get trackingUserValues(): number[] {
    return this.days.map((day) => day.totalActiveMinutes)
  }

  get activityValues(): number[] {
    return this.days.map((day) => day.completedLessonCount)
  }

  // A day before measurementStartDate has no real new-path data — its zero
  // is an absence-of-measurement, not "no activity" (spec: "旧方式による推定値").
  isLegacyEstimate(date: string): boolean {
    if (!this.measurementStartDate) return true
    return date < this.measurementStartDate
  }

  // Streak of consecutive active days ending at the last (most recent) day
  // in the fetched range. `days` is expected date-ascending (as returned by
  // the API), so this reads the currently-displayed range, not full history.
  get consecutiveDayCount(): number {
    let count = 0
    for (let i = this.days.length - 1; i >= 0; i--) {
      if (this.days[i].totalActiveSeconds <= 0) break
      count++
    }
    return count
  }
}
